import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getComplaintDetails,
    getComplaintEnums,
    changeComplaintStatus,
    assignComplaint,
    addComplaintComment,
    uploadComplaintAttachments,
    deleteComplaintAttachment,
    rateComplaint,
    getComplaintAuditLog,
    getComplaintCategories,
} from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import webTokenStorer from '../../../utils/webStorer';
import { notify } from '../../../utils/notification';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_COMPLAINTS,
    SOCIETY_PORTAL_COMPLAINTS_EDIT,
} from '../../../Routing/routes';
import { formatIstDate, formatIstDateTime } from '../../../utils/helpers/istDate';
import usePermissions from '../../../hook/usePermissions';
import { EMPLOYEE_ROSTER_PERMISSIONS } from '../../../consts/permissions';

const STATUS_STYLE = {
    PENDING: 'tw-bg-blue-100 tw-text-blue-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED: 'tw-bg-rose-100 tw-text-rose-700',
};

const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-amber-50 tw-text-amber-700',
    URGENT: 'tw-bg-rose-50 tw-text-rose-700',
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fullName = (u) => u
    ? ([u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || `#${u.user_id}`)
    : '—';

const fmtDate = (d) => d
    ? formatIstDateTime(d)
    : '—';

const bytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const resolveAssetUrl = (p) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    const base = process.env.REACT_APP_NODE_MOBILE_BASE_URL || '';
    return `${base.replace(/\/+$/, '')}${p.startsWith('/') ? '' : '/'}${p}`;
};

const isImage = (mime, name) => {
    if (mime && mime.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp)$/i.test(name || '');
};

// Mirrors the backend limits on POST /complaints/:id/comments so bad picks are
// rejected before the upload rather than coming back as a 400.
const MAX_COMMENT_FILES = 5;
const MAX_COMMENT_FILE_BYTES = 5 * 1024 * 1024;
const COMMENT_FILE_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt';
const ALLOWED_COMMENT_FILE_RE = /\.(png|jpe?g|gif|webp|bmp|svg|pdf|docx?|xlsx?|txt)$/i;

const isAllowedCommentFile = (file) =>
    (file.type || '').startsWith('image/') || ALLOWED_COMMENT_FILE_RE.test(file.name || '');

const ViewComplaint = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const fileInputRef = useRef(null);
    const commentFileInputRef = useRef(null);

    const {
        complaintDetailsData: c,
        complaintDetailsLoading,
        complaintEnumsData,
        statusChangeLoading,
        assignLoading,
        commentLoading,
        attachmentLoading,
        ratingLoading,
        auditLogLoading,
        auditLogData,
        categoriesData,
    } = useSelector(state => state.ComplaintReducer || {});
    const { employeesDropdownData } = useSelector(state => state.EmployeeReducer || {});
    // Assignee pool is the society's active employees (same source the create
    // form uses). Filter out any employee whose user link is missing — you
    // can't assign a complaint to a user_id that doesn't exist.
    const assignableEmployees = useMemo(() => {
        const rows = Array.isArray(employeesDropdownData) ? employeesDropdownData : [];
        return rows.filter(e => e.employee_id && e.user_id);
    }, [employeesDropdownData]);

    const currentUserId = webTokenStorer.getUserId();
    // Only the uploader may remove an attachment — everyone else just opens it.
    const isMyUpload = (att) => currentUserId != null && att?.uploaded_by != null && String(att.uploaded_by) === String(currentUserId);

    const [statusValue, setStatusValue] = useState('');
    const [statusNotes, setStatusNotes] = useState('');
    const [assignValue, setAssignValue] = useState('');
    const [commentText, setCommentText] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [isInternal, setIsInternal] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingFeedback, setRatingFeedback] = useState('');
    const [auditOpen, setAuditOpen] = useState(false);

    const refresh = () => dispatch(getComplaintDetails({ id, societyId }));

    useEffect(() => {
        if (id) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, societyId]);

    // Header refresh: the complaint (comments, attachments, timeline) and, when
    // it is open, the audit log. Enums, categories and employees are left alone.
    usePageRefresh(() => {
        refresh();
        if (auditOpen && societyId) dispatch(getComplaintAuditLog({ societyId, complaintId: id }));
    });

    useEffect(() => {
        if (!complaintEnumsData) dispatch(getComplaintEnums());
    }, [dispatch, complaintEnumsData]);

    // A refused request would replace this page with Access Denied.
    const { canAny } = usePermissions();
    useEffect(() => {
        if (societyId && canAny(EMPLOYEE_ROSTER_PERMISSIONS)) dispatch(getEmployeesDropdown({ societyId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societyId]);

    // Categories are pulled here so historical category IDs surfaced in the
    // audit log can be resolved back to human names — otherwise the diff
    // would only show numeric IDs for any category change.
    useEffect(() => {
        if (societyId && !categoriesData) dispatch(getComplaintCategories({ societyId }));
    }, [dispatch, societyId, categoriesData]);

    // Whenever the complaint or the roster loads, reverse-map the currently
    // assigned user_id back to the matching employee_id so the dropdown
    // pre-selects the right option. If no match (assigned to a non-employee
    // user, or roster still loading), the dropdown stays on "Unassigned".
    useEffect(() => {
        if (!c) return;
        if (!c.assigned_to_user_id) {
            setAssignValue('');
            return;
        }
        const match = assignableEmployees.find(e => e.user_id === c.assigned_to_user_id);
        setAssignValue(match?.employee_id ? String(match.employee_id) : '');
    }, [c, assignableEmployees]);

    const allowedNextStatuses = useMemo(() => {
        if (!c || !complaintEnumsData?.transitions) return [];
        return complaintEnumsData.transitions[c.status] || [];
    }, [c, complaintEnumsData]);

    // Lookups for the audit log — resolve user_id → name, category_id → name,
    // etc. Built here (rather than inside AuditEntry) so we only construct the
    // maps once per render, not once per row.
    const auditLookups = useMemo(() => {
        const users = new Map();
        const addUser = (u) => {
            if (!u?.user_id) return;
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
                || u.email || `User #${u.user_id}`;
            users.set(Number(u.user_id), name);
        };
        addUser(c?.raisedBy);
        addUser(c?.assignedTo);
        assignableEmployees.forEach(addUser);

        const categories = new Map();
        (Array.isArray(categoriesData) ? categoriesData : []).forEach(cat => {
            if (cat?.category_id) categories.set(Number(cat.category_id), cat.name);
        });
        if (c?.category?.category_id) {
            categories.set(Number(c.category.category_id), c.category.name);
        }

        return {
            userName: (id) => users.get(Number(id)),
            categoryName: (id) => categories.get(Number(id)),
        };
    }, [c, assignableEmployees, categoriesData]);

    const handleChangeStatus = () => {
        if (!statusValue) return;
        dispatch(changeComplaintStatus({
            id,
            status: statusValue,
            notes: statusNotes || undefined,
            onSuccess: () => {
                setStatusValue('');
                setStatusNotes('');
                refresh();
            },
        }));
    };

    const handleAssign = () => {
        // The FE keeps things in employee_id land (that's what the dropdown
        // shows) but the BE assign endpoint stores user_id. Resolve the pick
        // to its user_id before dispatching so no reverse-lookup is needed
        // on the server.
        const picked = assignValue
            ? assignableEmployees.find(e => String(e.employee_id) === String(assignValue))
            : null;
        dispatch(assignComplaint({
            id,
            assigned_to_user_id: picked?.user_id || null,
            onSuccess: () => refresh(),
        }));
    };

    // Either text or at least one file is enough — the BE accepts a comment
    // that is nothing but attachments.
    const canPostComment = Boolean(commentText.trim()) || commentFiles.length > 0;

    const handleAddComment = (e) => {
        e?.preventDefault?.();
        if (!canPostComment) return;
        dispatch(addComplaintComment({
            id,
            comment_text: commentText.trim() || undefined,
            is_internal: isInternal,
            files: commentFiles,
            onSuccess: () => {
                setCommentText('');
                setCommentFiles([]);
                setIsInternal(false);
                if (commentFileInputRef.current) commentFileInputRef.current.value = '';
                refresh();
            },
        }));
    };

    const handlePickCommentFiles = () => commentFileInputRef.current?.click();

    const handleCommentFilesSelected = (e) => {
        const picked = Array.from(e.target.files || []);
        // Reset immediately so re-picking the same file still fires onChange.
        if (commentFileInputRef.current) commentFileInputRef.current.value = '';
        if (picked.length === 0) return;

        const rejectedType = picked.filter(f => !isAllowedCommentFile(f));
        const rejectedSize = picked.filter(f => f.size > MAX_COMMENT_FILE_BYTES);
        if (rejectedType.length) {
            notify('error', 'Only image, PDF, Word, Excel, or TXT files are allowed');
        }
        if (rejectedSize.length) {
            notify('error', 'Each attachment must be under 5MB');
        }

        const accepted = picked.filter(f => isAllowedCommentFile(f) && f.size <= MAX_COMMENT_FILE_BYTES);
        if (accepted.length === 0) return;

        setCommentFiles(prev => {
            // De-dupe on name+size so double-picking the same file doesn't
            // burn one of the five slots.
            const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
            const merged = [...prev];
            accepted.forEach(f => {
                const key = `${f.name}:${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(f);
                }
            });
            if (merged.length > MAX_COMMENT_FILES) {
                notify('error', `Too many files. Max ${MAX_COMMENT_FILES} attachments per comment`);
                return merged.slice(0, MAX_COMMENT_FILES);
            }
            return merged;
        });
    };

    const removeCommentFile = (index) =>
        setCommentFiles(prev => prev.filter((_, i) => i !== index));

    const handlePickFiles = () => fileInputRef.current?.click();

    const handleUploadFiles = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        dispatch(uploadComplaintAttachments({
            id,
            files,
            onSuccess: () => {
                if (fileInputRef.current) fileInputRef.current.value = '';
                refresh();
            },
        }));
    };

    const openAudit = () => {
        setAuditOpen(true);
        if (societyId) {
            dispatch(getComplaintAuditLog({ societyId, complaintId: id }));
        }
    };

    const handleSubmitRating = () => {
        if (!ratingValue) return;
        dispatch(rateComplaint({
            id,
            rating: ratingValue,
            feedback: ratingFeedback.trim() || undefined,
            onSuccess: () => {
                setRatingValue(0);
                setRatingHover(0);
                setRatingFeedback('');
                refresh();
            },
        }));
    };

    const handleDeleteAttachment = (attachmentId) => {
        if (!window.confirm('Remove this attachment?')) return;
        dispatch(deleteComplaintAttachment({
            id,
            attachmentId,
            onSuccess: () => refresh(),
        }));
    };

    if (complaintDetailsLoading && !c) {
        return (
            <div className="tw-p-10 tw-flex tw-justify-center">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
            </div>
        );
    }
    if (!c) {
        return (
            <div className="tw-p-10 tw-text-center tw-text-xs tw-text-gray-400">Complaint not found.</div>
        );
    }

    const comments = Array.isArray(c.comments) ? c.comments : [];
    const attachments = Array.isArray(c.attachments) ? c.attachments : [];

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-6xl tw-mx-auto tw-space-y-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS)}
                        className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                    >
                        <Icon icon="mdi:arrow-left" /> Back to Complaints
                    </button>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={openAudit}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-purple-700 tw-border tw-border-purple-200 tw-bg-white hover:tw-bg-purple-50"
                        >
                            <Icon icon="mdi:history" /> Audit Log
                        </button>
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_COMPLAINTS_EDIT.replace(':id', id))}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-amber-600 hover:tw-bg-amber-700"
                        >
                            <Icon icon="mdi:pencil-outline" /> Edit
                        </button>
                    </div>
                </div>

                {/* Header card */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-5">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
                        <div className="tw-min-w-0 tw-flex-1">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-wrap">
                                <span className="tw-text-[11px] tw-font-mono tw-font-bold tw-text-gray-600 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded">
                                    {c.complaint_code}
                                </span>
                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[c.status] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                    {humanize(c.status)}
                                </span>
                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${PRIORITY_STYLE[c.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                    {humanize(c.priority)}
                                </span>
                                <span className="tw-text-[11px] tw-text-gray-500">{c.category?.name || 'Uncategorized'}</span>
                                {c.reopened_count > 0 && (
                                    <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-text-[10px] tw-text-rose-700 tw-bg-rose-50 tw-px-1.5 tw-py-0.5 tw-rounded">
                                        <Icon icon="mdi:refresh" /> Reopened {c.reopened_count}×
                                    </span>
                                )}
                                {c.escalated_at && c.status !== 'COMPLETED' && (
                                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-white tw-bg-rose-600 tw-px-1.5 tw-py-0.5 tw-rounded">
                                        <Icon icon="mdi:alert-decagram" /> Escalated
                                    </span>
                                )}
                            </div>
                            <h1 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mb-1">{c.subject}</h1>
                            <p className="tw-text-xs tw-text-gray-600 tw-whitespace-pre-wrap">{c.description}</p>
                        </div>
                    </div>

                    <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-100 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-[11px]">
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Raised By</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{fullName(c.raisedBy)}</p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Assigned To</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{fullName(c.assignedTo)}</p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Created</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{fmtDate(c.created_at)}</p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Wing</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{c.wing ? `Wing ${c.wing.title || c.wing.number}` : (c.wing_id ? `#${c.wing_id}` : '—')}</p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Flat</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{c.flat?.flat_number || (c.flat_id ? `#${c.flat_id}` : '—')}</p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Due Date</p>
                            <p className="tw-text-gray-800 tw-mt-0.5">{c.due_date ? formatIstDate(c.due_date) : '—'}</p>
                        </div>
                        {c.resolved_at && (
                            <div>
                                <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Completed</p>
                                <p className="tw-text-gray-800 tw-mt-0.5">{fmtDate(c.resolved_at)}</p>
                            </div>
                        )}
                        {c.resolution_notes && (
                            <div className="sm:tw-col-span-2 md:tw-col-span-4">
                                <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold">Resolution Notes</p>
                                <p className="tw-text-gray-800 tw-mt-0.5 tw-whitespace-pre-wrap">{c.resolution_notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    {/* Status change */}
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:swap-horizontal" className="tw-text-rose-600" /> Change Status
                        </h2>
                        {allowedNextStatuses.length === 0 ? (
                            <p className="tw-text-[11px] tw-text-gray-400">No further transitions available from <strong>{humanize(c.status)}</strong>.</p>
                        ) : (
                            <>
                                <select
                                    value={statusValue}
                                    onChange={e => setStatusValue(e.target.value)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-mb-2"
                                >
                                    <option value="">Select next status…</option>
                                    {allowedNextStatuses.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
                                </select>
                                <textarea
                                    rows={2}
                                    value={statusNotes}
                                    onChange={e => setStatusNotes(e.target.value)}
                                    placeholder="Optional notes / resolution details…"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-mb-2"
                                />
                                <button
                                    onClick={handleChangeStatus}
                                    disabled={statusChangeLoading || !statusValue}
                                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    {statusChangeLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                    Update Status
                                </button>
                            </>
                        )}
                    </div>

                    {/* Assign */}
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:account-arrow-right-outline" className="tw-text-rose-600" /> Assignment
                        </h2>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Assign to Employee</label>
                        <select
                            value={assignValue || ''}
                            onChange={e => setAssignValue(e.target.value)}
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-mb-2"
                        >
                            <option value="">Unassigned</option>
                            {assignableEmployees.map(u => {
                                const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
                                    || u.email || `Employee #${u.employee_id}`;
                                return (
                                    <option key={u.employee_id} value={u.employee_id}>{name}</option>
                                );
                            })}
                        </select>
                        {assignableEmployees.length === 0 && (
                            <p className="tw-text-[10px] tw-text-amber-600 tw-mb-2">
                                No active employees found for this society.
                            </p>
                        )}
                        <button
                            onClick={handleAssign}
                            disabled={assignLoading}
                            className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            {assignLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                            Save Assignment
                        </button>
                    </div>
                </div>

                {/* Attachments */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:paperclip" className="tw-text-rose-600" /> Attachments ({attachments.length})
                        </h2>
                        <button
                            onClick={handlePickFiles}
                            disabled={attachmentLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50"
                        >
                            {attachmentLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                            <Icon icon="mdi:upload" /> Upload
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="tw-hidden"
                            onChange={handleUploadFiles}
                        />
                    </div>
                    {attachments.length === 0 ? (
                        <p className="tw-text-[11px] tw-text-gray-400 tw-text-center tw-py-6">No attachments yet. Upload images or documents to support this complaint.</p>
                    ) : (
                        <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 tw-gap-3">
                            {attachments.map(att => {
                                const url = resolveAssetUrl(att.file_path);
                                const img = isImage(att.mime_type, att.file_name);
                                return (
                                    <div key={att.attachment_id} className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden tw-bg-gray-50 tw-flex tw-flex-col">
                                        <a href={url} target="_blank" rel="noreferrer" className="tw-block tw-h-24 tw-flex tw-items-center tw-justify-center tw-bg-white tw-border-b tw-border-gray-100">
                                            {img ? (
                                                <img src={url} alt={att.file_name} className="tw-h-full tw-w-full tw-object-cover" />
                                            ) : (
                                                <Icon icon="mdi:file-document-outline" className="tw-text-3xl tw-text-gray-400" />
                                            )}
                                        </a>
                                        <div className="tw-p-2">
                                            <p className="tw-text-[11px] tw-font-medium tw-text-gray-800 tw-truncate" title={att.file_name}>{att.file_name}</p>
                                            <p className="tw-text-[10px] tw-text-gray-500">{bytes(att.size_bytes)}</p>
                                            <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
                                                <a href={url} target="_blank" rel="noreferrer" className="tw-text-[10px] tw-text-blue-600 hover:tw-underline">Open</a>
                                                {isMyUpload(att) && (
                                                    <button
                                                        onClick={() => handleDeleteAttachment(att.attachment_id)}
                                                        disabled={attachmentLoading}
                                                        className="tw-text-[10px] tw-text-rose-600 hover:tw-underline disabled:tw-opacity-50"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Rating — only visible when COMPLETED. Shows a widget for the
                    raiser if not yet rated, otherwise renders the received rating. */}
                {c.status === 'COMPLETED' && (c.rating || currentUserId === c.raised_by_user_id) && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:star-outline" className="tw-text-amber-500" /> Feedback
                        </h2>
                        {c.rating ? (
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <div className="tw-flex">
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <Icon key={n} icon={n <= c.rating ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-2xl tw-text-amber-400" />
                                        ))}
                                    </div>
                                    <span className="tw-text-xs tw-text-gray-600">{c.rating}/5</span>
                                    {c.rated_at && (
                                        <span className="tw-text-[10px] tw-text-gray-400">· submitted {fmtDate(c.rated_at)}</span>
                                    )}
                                </div>
                                {c.rating_feedback && (
                                    <p className="tw-mt-3 tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-100 tw-rounded-md tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap">
                                        {c.rating_feedback}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div>
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mb-2">How well was this complaint resolved?</p>
                                <div
                                    className="tw-flex tw-items-center tw-gap-1"
                                    onMouseLeave={() => setRatingHover(0)}
                                >
                                    {[1, 2, 3, 4, 5].map(n => {
                                        const filled = (ratingHover || ratingValue) >= n;
                                        return (
                                            <button
                                                type="button"
                                                key={n}
                                                onMouseEnter={() => setRatingHover(n)}
                                                onClick={() => setRatingValue(n)}
                                                className="tw-p-0.5 tw-rounded hover:tw-scale-110 tw-transition-transform"
                                                aria-label={`${n} star${n === 1 ? '' : 's'}`}
                                            >
                                                <Icon icon={filled ? 'mdi:star' : 'mdi:star-outline'} className="tw-text-3xl tw-text-amber-400" />
                                            </button>
                                        );
                                    })}
                                    {ratingValue > 0 && (
                                        <span className="tw-ml-2 tw-text-xs tw-text-gray-600">{ratingValue}/5</span>
                                    )}
                                </div>
                                <textarea
                                    rows={3}
                                    value={ratingFeedback}
                                    onChange={e => setRatingFeedback(e.target.value)}
                                    placeholder="Anything you'd like the society team to know? (optional)"
                                    className="tw-w-full tw-mt-3 tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-amber-500"
                                />
                                <div className="tw-flex tw-justify-end tw-mt-2">
                                    <button
                                        onClick={handleSubmitRating}
                                        disabled={ratingLoading || !ratingValue}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-amber-500 hover:tw-bg-amber-600 tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                    >
                                        {ratingLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                        <Icon icon="mdi:send" /> Submit Rating
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Comments */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:comment-text-outline" className="tw-text-rose-600" /> Comments &amp; Activity ({comments.length})
                    </h2>

                    <div className="tw-space-y-3 tw-mb-4 tw-max-h-96 tw-overflow-y-auto tw-pr-1">
                        {comments.length === 0 && (
                            <p className="tw-text-[11px] tw-text-gray-400 tw-text-center tw-py-4">No comments or status changes yet.</p>
                        )}
                        {comments.map((cm) => {
                            const isStatus = cm.old_status && cm.new_status;
                            return (
                                <div key={cm.comment_id} className={`tw-border tw-rounded-lg tw-p-3 ${cm.is_internal ? 'tw-bg-amber-50/50 tw-border-amber-100' : 'tw-bg-white tw-border-gray-100'}`}>
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-1">
                                        <span className="tw-text-[11px] tw-font-bold tw-text-gray-800">{fullName(cm.user)}</span>
                                        <span className="tw-text-[10px] tw-text-gray-500">{fmtDate(cm.created_at)}</span>
                                        {cm.is_internal ? (
                                            <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-text-[9px] tw-text-amber-800 tw-bg-amber-100 tw-px-1.5 tw-py-0.5 tw-rounded">
                                                <Icon icon="mdi:lock-outline" /> Internal
                                            </span>
                                        ) : null}
                                        {isStatus && (
                                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-600">
                                                <span className={`tw-px-1.5 tw-py-0.5 tw-rounded ${STATUS_STYLE[cm.old_status] || 'tw-bg-gray-100'}`}>{humanize(cm.old_status)}</span>
                                                <Icon icon="mdi:arrow-right" />
                                                <span className={`tw-px-1.5 tw-py-0.5 tw-rounded ${STATUS_STYLE[cm.new_status] || 'tw-bg-gray-100'}`}>{humanize(cm.new_status)}</span>
                                            </span>
                                        )}
                                    </div>
                                    {cm.comment_text && (
                                        <p className="tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap">{cm.comment_text}</p>
                                    )}
                                    {Array.isArray(cm.attachments) && cm.attachments.length > 0 && (
                                        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
                                            {cm.attachments.map(att => {
                                                const url = resolveAssetUrl(att.file_path);
                                                const img = isImage(att.mime_type, att.file_name);
                                                return (
                                                    <a
                                                        key={att.attachment_id}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={att.file_name}
                                                        className="tw-flex tw-items-center tw-gap-1.5 tw-max-w-[180px] tw-border tw-border-gray-200 tw-rounded-md tw-bg-white tw-pr-2 hover:tw-border-rose-300 hover:tw-bg-rose-50/40"
                                                    >
                                                        {img ? (
                                                            <img src={url} alt={att.file_name} className="tw-h-9 tw-w-9 tw-object-cover tw-rounded-l-md" />
                                                        ) : (
                                                            <span className="tw-h-9 tw-w-9 tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-rounded-l-md">
                                                                <Icon icon="mdi:file-document-outline" className="tw-text-base tw-text-gray-400" />
                                                            </span>
                                                        )}
                                                        <span className="tw-min-w-0 tw-py-1">
                                                            <span className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-800 tw-truncate">{att.file_name}</span>
                                                            <span className="tw-block tw-text-[9px] tw-text-gray-500">{bytes(att.size_bytes)}</span>
                                                        </span>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Add comment */}
                    <form onSubmit={handleAddComment} className="tw-border-t tw-border-gray-100 tw-pt-3">
                        <textarea
                            rows={3}
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            maxLength={2000}
                            placeholder="Add a comment…"
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-mb-2"
                        />

                        {/* Files staged for this comment — nothing is uploaded
                            until the comment itself is posted. */}
                        {commentFiles.length > 0 && (
                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
                                {commentFiles.map((f, i) => (
                                    <span
                                        key={`${f.name}-${f.size}-${i}`}
                                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-[200px] tw-pl-2 tw-pr-1 tw-py-1 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md"
                                    >
                                        <Icon
                                            icon={f.type?.startsWith('image/') ? 'mdi:image-outline' : 'mdi:file-document-outline'}
                                            className="tw-text-sm tw-text-gray-400 tw-shrink-0"
                                        />
                                        <span className="tw-text-[10px] tw-text-gray-700 tw-truncate" title={f.name}>{f.name}</span>
                                        <span className="tw-text-[9px] tw-text-gray-400 tw-shrink-0">{bytes(f.size)}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeCommentFile(i)}
                                            className="tw-p-0.5 tw-rounded hover:tw-bg-gray-200 tw-text-gray-500 tw-shrink-0"
                                            aria-label={`Remove ${f.name}`}
                                        >
                                            <Icon icon="mdi:close" className="tw-text-xs" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
                            <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                                <label className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={isInternal}
                                        onChange={e => setIsInternal(e.target.checked)}
                                        className="tw-rounded"
                                    />
                                    Internal note (only visible to staff)
                                </label>
                                <button
                                    type="button"
                                    onClick={handlePickCommentFiles}
                                    disabled={commentLoading || commentFiles.length >= MAX_COMMENT_FILES}
                                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-gray-600 hover:tw-text-rose-600 disabled:tw-opacity-50 disabled:hover:tw-text-gray-600"
                                >
                                    <Icon icon="mdi:paperclip" /> Attach files
                                </button>
                                <span className="tw-text-[10px] tw-text-gray-400">
                                    {commentFiles.length}/{MAX_COMMENT_FILES} · max 5MB each
                                </span>
                                <input
                                    ref={commentFileInputRef}
                                    type="file"
                                    multiple
                                    accept={COMMENT_FILE_ACCEPT}
                                    className="tw-hidden"
                                    onChange={handleCommentFilesSelected}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={commentLoading || !canPostComment}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {commentLoading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                <Icon icon="mdi:send" /> Post
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Audit log modal — mirrors the Employees audit modal pattern */}
            {auditOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-3xl tw-max-h-[85vh] tw-flex tw-flex-col tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-purple-50 tw-text-purple-600 tw-flex tw-items-center tw-justify-center">
                                    <Icon icon="mdi:history" className="tw-text-lg" />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Audit Log — {c.complaint_code}</h3>
                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Every action taken on this complaint and its comments / attachments</p>
                                </div>
                            </div>
                            <button onClick={() => setAuditOpen(false)} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                                <Icon icon="mdi:close" className="tw-text-lg" />
                            </button>
                        </div>
                        <div className="tw-p-5 tw-overflow-y-auto">
                            {auditLogLoading && (
                                <div className="tw-flex tw-justify-center tw-py-12">
                                    <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-purple-500" />
                                </div>
                            )}
                            {!auditLogLoading && (!auditLogData || auditLogData.length === 0) && (
                                <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">
                                    No audit entries yet.
                                </div>
                            )}
                            {!auditLogLoading && (auditLogData || []).map((log, i) => (
                                <AuditEntry key={log.audit_id ?? i} log={log} lookups={auditLookups} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Single audit row — an inline component so ViewComplaint stays self-contained.
// Renders action badge, actor/when, request URL, and an expandable diff of the
// changed fields when we have both old and new values (typical for UPDATE).
const AUDIT_ACTION_STYLE = {
    CREATE: 'tw-bg-emerald-100 tw-text-emerald-700',
    UPDATE: 'tw-bg-amber-100 tw-text-amber-700',
    DELETE: 'tw-bg-rose-100 tw-text-rose-700',
    OTHER:  'tw-bg-purple-100 tw-text-purple-700',
};

// Field-name → human label. Anything not listed falls back to a Title Case
// version of the raw column name.
const FIELD_LABELS = {
    status: 'Status',
    priority: 'Priority',
    subject: 'Subject',
    description: 'Description',
    due_date: 'Due Date',
    resolution_notes: 'Resolution Notes',
    resolved_at: 'Resolved At',
    closed_at: 'Closed At',
    reopened_count: 'Reopened Count',
    escalated_at: 'Escalated At',
    assigned_to_user_id: 'Assigned To',
    assigned_at: 'Assigned At',
    raised_by_user_id: 'Raised By',
    uploaded_by: 'Uploaded By',
    created_by: 'Created By',
    updated_by: 'Updated By',
    category_id: 'Category',
    flat_id: 'Flat',
    wing_id: 'Wing',
    society_id: 'Society',
    complaint_id: 'Complaint',
    complaint_code: 'Complaint Code',
    is_active: 'Active',
    is_internal: 'Internal Note',
    comment_text: 'Comment',
    old_status: 'Previous Status',
    new_status: 'New Status',
    rating: 'Rating',
    rating_feedback: 'Feedback',
    rated_at: 'Rated At',
    file_name: 'File Name',
    file_path: 'File',
    mime_type: 'File Type',
    size_bytes: 'File Size',
    created_at: 'Created At',
    updated_at: 'Updated At',
};

const ENTITY_LABELS = {
    complaints: 'Complaint',
    complaint_comments: 'Comment',
    complaint_attachments: 'Attachment',
    complaint_categories: 'Category',
};

const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const labelFor = (key) => FIELD_LABELS[key] || titleCase(key);
const entityLabelFor = (t) => ENTITY_LABELS[t] || titleCase(t);

// Fields we always hide from the diff — internal bookkeeping the user doesn't
// need to see and would only add noise.
const IGNORED_KEYS = new Set(['updated_at', 'created_at', 'updated_by', 'uuid']);

const USER_ID_KEYS = new Set([
    'assigned_to_user_id', 'raised_by_user_id', 'uploaded_by',
    'created_by', 'updated_by', 'user_id'
]);

const formatBytes = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return String(n);
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

const formatAuditValue = (key, value, lookups = {}) => {
    if (value === null || value === undefined || value === '') return '—';
    if (key === 'is_active' || key === 'is_internal') {
        return (value === 1 || value === true || value === '1' || value === 'true') ? 'Yes' : 'No';
    }
    if (key === 'status' || key === 'old_status' || key === 'new_status' || key === 'priority') {
        return titleCase(String(value));
    }
    if (USER_ID_KEYS.has(key)) {
        return lookups.userName?.(value) || `User #${value}`;
    }
    if (key === 'category_id') return lookups.categoryName?.(value) || `Category #${value}`;
    if (key === 'flat_id') return `Flat #${value}`;
    if (key === 'wing_id') return `Wing #${value}`;
    if (key === 'size_bytes') return formatBytes(value);
    if (/_at$/.test(key) || key === 'due_date') {
        try {
            const isDateOnly = typeof value === 'string' && value.length <= 10;
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return String(value);
            return isDateOnly
                ? formatIstDate(d)
                : formatIstDateTime(d);
        } catch { return String(value); }
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

// The audit_logs table stores old/new values + changed_fields as JSON columns,
// but some deployments (or a plain SELECT that bypasses model hydration)
// deliver them as raw strings. Iterating a string via Object.keys(...) yields
// character indices — that's what produced "one change per character" in the
// list before this fix. safeJson coerces strings to objects/arrays and leaves
// already-parsed values untouched.
const safeJson = (v, fallback) => {
    if (v == null) return fallback;
    if (typeof v !== 'string') return v;
    try { return JSON.parse(v); } catch { return fallback; }
};

const AuditEntry = ({ log, lookups = {} }) => {
    const [expanded, setExpanded] = useState(false);
    const action = (log.action || '').toUpperCase();
    const when = log.created_at || log.timestamp;
    const whenFmt = when ? formatIstDateTime(when) : '—';
    const by = log.user
        ? ([log.user.first_name, log.user.last_name].filter(Boolean).join(' ').trim() || log.user.email || `#${log.user.user_id}`)
        : (log.description?.includes('escalation-cron') ? 'system' : '—');

    const oldVals = safeJson(log.old_values, {}) || {};
    const newVals = safeJson(log.new_values, {}) || {};
    const changedRaw = safeJson(log.changed_fields, null);
    const changed = Array.isArray(changedRaw) && changedRaw.length > 0
        ? changedRaw.filter(k => !IGNORED_KEYS.has(k))
        : Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]))
            .filter(k => !IGNORED_KEYS.has(k))
            .filter(k => JSON.stringify(oldVals[k]) !== JSON.stringify(newVals[k]));

    return (
        <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 tw-mb-3 hover:tw-border-gray-200">
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-1.5">
                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-rounded-full ${AUDIT_ACTION_STYLE[action] || 'tw-bg-gray-100 tw-text-gray-700'}`}>
                    {action || 'EVENT'}
                </span>
                <span className="tw-text-[11px] tw-text-gray-700">{entityLabelFor(log.entity_type)}</span>
                <span className="tw-text-[11px] tw-text-gray-600">{whenFmt}</span>
                <span className="tw-text-[11px] tw-text-gray-400">·</span>
                <span className="tw-text-[11px] tw-text-gray-700">by {by}</span>
                {log.ip_address && (
                    <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono tw-ml-auto">IP {log.ip_address}</span>
                )}
            </div>
            {log.description && (
                <p className="tw-text-xs tw-text-gray-700 tw-mb-1.5">{log.description}</p>
            )}
            {(log.request_method || log.request_url) && (
                <p className="tw-text-[10px] tw-text-gray-400 tw-font-mono tw-mb-2">
                    {log.request_method} {log.request_url}
                </p>
            )}
            {changed.length > 0 && (
                <button
                    onClick={() => setExpanded(s => !s)}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-blue-600 hover:tw-text-blue-800"
                >
                    <Icon icon={expanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} />
                    {expanded ? 'Hide' : 'Show'} {changed.length} {changed.length === 1 ? 'change' : 'changes'}
                </button>
            )}
            {expanded && changed.length > 0 && (
                <div className="tw-mt-2 tw-border-t tw-border-gray-100 tw-pt-2 tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-[10px]">
                        <thead>
                            <tr className="tw-text-gray-400 tw-uppercase tw-tracking-wide">
                                <th className="tw-text-left tw-py-1.5 tw-pr-3 tw-font-bold">Field</th>
                                <th className="tw-text-left tw-py-1.5 tw-pr-3 tw-font-bold">Before</th>
                                <th className="tw-text-left tw-py-1.5 tw-font-bold">After</th>
                            </tr>
                        </thead>
                        <tbody>
                            {changed.map(key => (
                                <tr key={key} className="tw-border-t tw-border-gray-50">
                                    <td className="tw-py-1.5 tw-pr-3 tw-text-gray-700 tw-font-semibold tw-whitespace-nowrap">{labelFor(key)}</td>
                                    <td className="tw-py-1.5 tw-pr-3 tw-text-rose-700 tw-break-all">{formatAuditValue(key, oldVals[key], lookups)}</td>
                                    <td className="tw-py-1.5 tw-text-emerald-700 tw-break-all">{formatAuditValue(key, newVals[key], lookups)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ViewComplaint;
